import _ from "underscore";
import FontProvider  from "shared/data/fonts/FontProvider";
import Preconditions from "shared/util/Preconditions";

const FONT_WEIGHT_MAP = {
    "normal": "400",
    "bold": "700"
};

interface FontVariant {
    value: string;
    label: string;
    weight: string;
    style?: string; // "normal" | "italic";
}

const providerToVariants = new Map<FontProvider.FontProvider, Array<FontVariant>>();

providerToVariants.set(FontProvider.STANDARD, ((): Array<FontVariant> =>
    _.reduce([
        {weight: "400", label: "Normal"},
        {weight: "700", label: "Bold"}
        ],
             (arrayMemo, seed) => {
            arrayMemo.push({
                value: seed.weight,
                label: `${seed.label} (${seed.weight})`,
                weight: seed.weight,
                style: "normal"
            });
            arrayMemo.push({
                value: `${seed.weight}italic`,
                label: `${seed.label} Italic (${seed.weight})`,
                weight: seed.weight,
                style: "italic"
            });

            return arrayMemo;
        },
             [])
)());

providerToVariants.set(FontProvider.GOOGLE, (() =>
    _.reduce([
        {weight: "100", label: "Thin"},
        {weight: "200", label: "Extra-Light"},
        {weight: "300", label: "Light"},
        {weight: "400", label: "Normal"},
        {weight: "500", label: "Medium"},
        {weight: "600", label: "Semi-Bold"},
        {weight: "700", label: "Bold"},
        {weight: "800", label: "Extra-Bold"},
        {weight: "900", label: "Ultra-Bold"}
    ],       (arrayMemo, seed) => {
        arrayMemo.push({
            value: seed.weight === "400" ? "regular" : seed.weight,
            label: `${seed.label} ${seed.weight}`,
            weight: seed.weight,
            style: "normal"
        });
        arrayMemo.push({
            value: seed.weight === "400" ? "italic" : `${seed.weight}italic`,
            label: `${seed.label} ${seed.weight} Italic`,
            weight: seed.weight,
            style: "italic"
        });

        return arrayMemo;
    },       [])
)());

providerToVariants.set(FontProvider.TYPEKIT, (() =>
    _.reduce([
        {weight: "100", label: "Thin"},
        {weight: "300", label: "Light"},
        {weight: "400", label: "Regular"},
        {weight: "600", label: "Semibold"},
        {weight: "700", label: "Bold"},
        {weight: "800", label: "Extra Bold"},
        {weight: "900", label: "Heavy"}
    ],       (arrayMemo, seed) => {
        // Typekit variants come in the format n1, n3, n4, i1, i3 etc.
        const idPostfix = seed.weight.charAt(0);

        arrayMemo.push({
            value: `n${idPostfix}`,
            label: `${seed.label} ${seed.weight}`,
            weight: seed.weight,
            style: "normal"
        });
        arrayMemo.push({
            value: `i${idPostfix}`,
            label: `${seed.label} ${seed.weight} Italic`,
            weight: seed.weight,
            style: "italic"
        });

        return arrayMemo;
    },       [])
)());

providerToVariants.set(FontProvider.FONTS_DOT_COM, (() => {
        // Returning a single variation and style for all fonts from Fonts.com because Fonts.com
        // do not automatically group variations of fonts (WTF!?). For now we only support
        // ungrouped font projects.
        return [
            {
                value: "400",
                label: "Normal (400)",
                weight: "400",
                style: "normal"
            }
        ];
    }
)());

providerToVariants.set(FontProvider.CUSTOM, (() =>
    // Similar to `FontProvider.FONTS_DOT_COM` above
    [
        {
            value: "400",
            label: "Normal (400)",
            weight: "400",
            style: "normal"
        }
    ]
)());

/**
 * Utility for dealing with font variations.
 *
 * @see  #get
 *
 * @class WebFontVariants
 * @author Kim
 */
const WebFontVariants = {

    /**
     * Returns a display friendly array of variants objects for the specified <tt>FontProvider</tt>.
     *
     * @param  {FontProvider} fontProvider The <tt>FontProvider</tt>.
     * @return {Object[]}                  Array of variant objects, e.g.:
     * <pre>
     * {
     *     value: "700italic",
     *     label: "Bold 700 Italic",
     *     weight: 700,
     *     style: "italic"
     * }
     * </pre>
     *
     * The variant <tt>value</tt> matches that of the associated font API, and is the same value that
     * is being persisted for text widget models <tt>fontStyle</tt>, e.g. <tt>700italic</tt>.
     */
    get(fontProvider: FontProvider.FontProvider): Array<FontVariant> {
        Preconditions.instanceOf(fontProvider, FontProvider, "Must be a FontProvider instance");
        return providerToVariants.get(fontProvider);
    },

    /**
     * findVariant
     *
     * @param {Object} fontProvider
     * @param {String} fontStyle
     * @param {String} fontWeight
     * @return {Object}
     */
    findVariant(fontProvider: FontProvider.FontProvider, fontStyle: string, fontWeight: string) {
        const variants = this.get(fontProvider);
        fontWeight = FONT_WEIGHT_MAP[fontWeight] || fontWeight;
        return _.find(variants, (variant: FontVariant) => variant.weight === fontWeight && variant.style === fontStyle);
    },

    /**
     * From a list of raw variant values, e.g. ["n1", "n3", "n4"], returns an array of the
     * matching variant objects.
     *
     * @param  {String[]} values        Array of raw variant values.
     * @param  {FontProvider} provider  The <tt>FontProvider</tt> associated with the specified variants.
     * @return {Object[]}               Array of the matching variant objects.
     */
    fromValues(values: Array<String>, provider: FontProvider.FontProvider) {
        return _.filter(WebFontVariants.get(provider), (variant) => _.contains(values, variant.value));
    },

    fromValue(value: string, provider: FontProvider.FontProvider) {
        return _.first(WebFontVariants.fromValues([value], provider));
    },

    /**
     * Returns the default variant descriptor for a specified font provider.
     *
     * @param  {FontProvider} provider The <tt>FontProvider</tt>.
     * @return {Object}                The "default" variant object, i.e. <tt>normal</tt> font
     *                                 style and a font weight of <tt>400</tt>.
     */
    defaultVariant(provider: FontProvider.FontProvider) {
        return _.find(
            WebFontVariants.get(provider), (variant) => variant.style === "normal" && variant.weight === "400");
    }

};

export {
    WebFontVariants as default,
    WebFontVariants,
};



// WEBPACK FOOTER //
// ./src/shared/ui/experience/fonts/WebFontVariants.ts